import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import projectTeamService from '@services/projectTeamService'

/**
 * RoleAssignment Component
 *
 * Displays a single role with a user dropdown for assignment
 *
 * @param {Object} props
 * @param {number} props.projectId - Project ID
 * @param {Object} props.role - Role object with role_id, role_name, role_slug
 * @param {number} props.selectedUserId - Currently selected user ID
 * @param {Function} props.onChange - Callback when selection changes
 */
const RoleAssignment = ({ projectId, role, selectedUserId, onChange }) => {
  const [availableUsers, setAvailableUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchAvailableUsers()
  }, [projectId, role.role_id])

  const fetchAvailableUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await projectTeamService.getAvailableUsers(projectId, role.role_id)
      setAvailableUsers(response)
    } catch (err) {
      console.error('Failed to fetch available users:', err)
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const userId = e.target.value ? parseInt(e.target.value) : null
    onChange(userId)
  }

  const isRequired = role.role_slug === 'project-manager' || role.role_slug === 'executive'

  return (
    <div className="role-assignment">
      <label className="role-assignment__label">
        {role.role_name}
        {isRequired && <span className="role-assignment__required">*</span>}
      </label>

      {loading ? (
        <div className="role-assignment__loading">
          <div className="spinner-small"></div>
          <span>Loading...</span>
        </div>
      ) : error ? (
        <div className="role-assignment__error">
          <span>{error}</span>
          <button className="role-assignment__retry" onClick={fetchAvailableUsers}>
            Retry
          </button>
        </div>
      ) : availableUsers.length === 0 ? (
        <div className="role-assignment__no-users">
          No users available for this role
        </div>
      ) : (
        <select
          className="role-assignment__select"
          value={selectedUserId || ''}
          onChange={handleChange}
        >
          <option value="">-- Select User --</option>
          {availableUsers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.first_name} {user.last_name}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

RoleAssignment.propTypes = {
  projectId: PropTypes.number.isRequired,
  role: PropTypes.shape({
    role_id: PropTypes.number.isRequired,
    role_name: PropTypes.string.isRequired,
    role_slug: PropTypes.string.isRequired,
    role_description: PropTypes.string,
    assigned_user: PropTypes.object,
  }).isRequired,
  selectedUserId: PropTypes.number,
  onChange: PropTypes.func.isRequired,
}

export default RoleAssignment
